import { FC, useContext, useMemo } from 'react';
import {
  IconButton,
  Divider,
  Stack,
  Paper,
  Collapse,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import OpenInNew from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSnackbar } from 'notistack';
import { DICTIONARY } from '@/constants/dictionary';
import { useOpsUiUrl } from '@/service/hooks/useOpsUiUrl';
import { RolePermissionsContext } from '@/contexts/rolePermissions/context';
import { useFixtureDetails } from '@/service/hooks/useFixtureDetails';
import { hasPermissionsToElement } from '@/components/PermissionsChecker/utils';
import { FixtureDetailsChip } from '@/components/common/FixtureDetailsChip';
import { ScoringContext } from '@/contexts/scoring/context';
import { copyToClipboard } from '@/utils';
import { CustomTooltip } from '../StatsCompareTable/StyledStatsComponents';

export interface FixtureInfoProps {
  fixtureId: string;
  isOpen: boolean;
}
export const COLLECTION_ID_COPY_BUTOON = 'collection-id-copy-button';

export const FixtureInfo: FC<FixtureInfoProps> = ({ fixtureId, isOpen }) => {
  const { permissions } = useContext(RolePermissionsContext);
  const { data: fixtureInfo, error } = useFixtureDetails();
  const { data: opsUiUrl } = useOpsUiUrl({
    fixtureId,
  });
  const {
    state: { fixtureSummary },
  } = useContext(ScoringContext);
  const { enqueueSnackbar } = useSnackbar();

  const errorTitle = error?.title;
  const errorMessage = error?.message;
  const content = useMemo(() => {
    if (errorMessage || errorTitle) {
      return (
        <Box>
          {errorTitle && (
            <Typography variant='overline'>{errorTitle}</Typography>
          )}
          {errorMessage && (
            <Typography fontWeight={700} variant='body1'>
              {errorMessage}
            </Typography>
          )}
        </Box>
      );
    }

    const handleCopyButtonClick = () => {
      fixtureSummary?.collectionId &&
        copyToClipboard(fixtureSummary?.collectionId);

      enqueueSnackbar(DICTIONARY.SUCCESS.COPIED_TO_CLIPBOARD, {
        variant: 'success',
      });
    };

    if (!fixtureInfo.details) return <CircularProgress />;

    return fixtureInfo.details?.map(
      ({ label, firstLine, secondLine, events, competitionChips }, column) => (
        <Box
          key={label}
          py={1}
          flex='1 1 0'
          style={column === 0 ? { flexGrow: 2 } : undefined}
          data-testid='fixture-info-column'
        >
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography py={1} variant='caption' lineHeight={1}>
              {label}
            </Typography>
            {column === 0 &&
              !!opsUiUrl &&
              hasPermissionsToElement(
                'FixturePage.HeaderPanel.FixtureInfo.DDEAccess',
                permissions,
              ) && (
                <Stack>
                  <CustomTooltip
                    title={DICTIONARY.COMMON.GO_TO_CONSOLE_TOOLTIP}
                  >
                    <IconButton
                      color='primary'
                      sx={{
                        p: 0,
                      }}
                      target='_blank'
                      href={opsUiUrl}
                      size='small'
                    >
                      <OpenInNew fontSize='inherit' />
                    </IconButton>
                  </CustomTooltip>
                </Stack>
              )}
          </Stack>
          {events ? (
            <Stack direction='row' gap={0.35} flexWrap='wrap'>
              {events?.map(
                (event) =>
                  event.visible && (
                    <FixtureDetailsChip
                      key={`event-${event.chipName}`}
                      label={event.chipName}
                      backgroundColor={event.color}
                    />
                  ),
              )}
            </Stack>
          ) : (
            <Stack gap={0.5}>
              <Stack
                flexDirection={column === 0 ? 'row' : 'column'}
                justifyContent={column === 0 ? 'space-between' : 'inherit'}
                alignItems='start'
              >
                <Typography lineHeight={1.1} variant='body1'>
                  {firstLine}
                </Typography>
                {column === 0 && (
                  <CustomTooltip
                    title={DICTIONARY.COMMON.COLLECTION_ID_COPY_TOOLTIP}
                  >
                    <IconButton
                      aria-label={COLLECTION_ID_COPY_BUTOON}
                      color='primary'
                      sx={{
                        p: '0',
                      }}
                      onClick={handleCopyButtonClick}
                    >
                      <ContentCopyIcon
                        sx={{
                          p: '0',
                          fontSize: (theme) => theme.typography.body1,
                        }}
                      />
                    </IconButton>
                  </CustomTooltip>
                )}
              </Stack>
              <Typography lineHeight={1.1} variant='body1'>
                {secondLine}
              </Typography>
            </Stack>
          )}
          {competitionChips && (
            <Stack gap={0.35}>
              {competitionChips.map(
                ({ visible, chipName, color }) =>
                  visible && (
                    <FixtureDetailsChip
                      key={chipName}
                      label={chipName}
                      backgroundColor={color}
                    />
                  ),
              )}
            </Stack>
          )}
        </Box>
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage, errorTitle, fixtureInfo, opsUiUrl, permissions]);

  return (
    <Paper sx={{ width: '100%' }} square>
      <Collapse in={isOpen}>
        <Stack
          direction='row'
          justifyContent='stretch'
          divider={<Divider orientation='vertical' flexItem />}
          gap={1}
          px={1}
        >
          {!fixtureId ? DICTIONARY.ERROR.FIXTURE_ID_MISSING : content}
        </Stack>
      </Collapse>
    </Paper>
  );
};
